// src/views/Admin/Locales/LocalPage/LocalPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HiArrowLeft, HiOutlinePencilSquare, HiOutlineTrash, HiOutlinePower } from 'react-icons/hi2';
import { HiOutlineCurrencyDollar, HiOutlineDocumentText } from 'react-icons/hi2';
import { getLocalByLocalId, DeleteLocalPermanently, ToggleLocalState } from '../../../../crud/management_api/locales';
import { getLastLocalSessionByLocalId } from '../../../../crud/management_api/local_sessions';
import { getRealTimeSalesStatisticsByLocalId, getHistoricalSalesStatisticsByLocalId } from '../../../../crud/management_api/sales_statistics';
import { getUsersByLocalId } from '../../../../crud/users_api/users';
import { getPuestosByLocalId } from '../../../../crud/management_api/puestos';
import MiniLoadingSpinner from '../../../../components/Spinners/MiniLoadingSpinner';
import {
  LocalHero,
  ActiveSessionCard,
  SalesStatsCard,
  UsersGrid,
  SessionsTable,
  DeleteLocalModal,
  ToggleStateModal,
  CreatePuestoModal,
  CreateUserModal,
  PaymentHistoryFolder
} from './components';
import type { PaymentHistoryFolderRef } from './components';
import CreateLocalPlan from '../../../Admin/LocalPlans/components/CreateLocalPlan/CreateLocalPlan';
import CreateLocalPlanPayment from '../../../Admin/LocalPlans/components/CreateLocalPlanPayment/CreateLocalPlanPayment';
import './LocalPage.scss';

// ============= Types =============
interface LocalBasicInfo {
  local_id: number;
  local_name: string;
  state: boolean | number;
}

interface SessionData {
  local_session_id: number;
  session_tag: string | null;
  local_id: number;
  user_id: number;
  username: string;
  session_start: string;
  session_end: string | null;
  active: boolean;
  active_shift_id: number | null;
  shifts: Array<{
    shift_id: number;
    session_id: number;
    active: boolean;
    name: string;
    start_time: string;
    end_time: string | null;
    open_user_id: number;
    open_username: string;
    close_user_id: number | null;
    close_username: string | null;
  }>;
}

interface RealTimeSalesData {
  total_charged: number;
  gross_sales: number;
  valid_receipt_count: number;
  users_count: number;
  net_sales: number;
  pending_payments_count: number;
  total_pending: number;
  cancelled_receipt_count: number;
  avg_ticket: number;
  total_tip: number;
  total_discounts: number;
}

interface HistoricalSession {
  sales_total_id: number;
  session_id: number;
  timestamp: string;
  start_timestamp: string;
  end_timestamp: string;
  total_charged: number;
  gross_sales: number;
  valid_receipt_count: number;
  net_sales: number;
  total_tip: number;
}

interface UserData {
  user_id: number;
  username: string;
  middle_name: string;
  last_name: string;
  email: string;
  state: number;
  created_at: string;
  rol_id?: number;
  rol_name?: string;
  puesto_id?: number;
  puesto_name?: string;
  password?: string; // Only available for admin users
}

interface PuestoData {
  puesto_id: number;
  puesto_name: string;
  local_id: number;
}

// ============= Component =============
const LocalPage: React.FC = () => {
  const { localId } = useParams<{ localId: string }>();
  const navigate = useNavigate();
  
  // Data states
  const [localData, setLocalData] = useState<LocalBasicInfo | null>(null);
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [realTimeSales, setRealTimeSales] = useState<RealTimeSalesData | null>(null);
  const [historicalData, setHistoricalData] = useState<HistoricalSession[]>([]);
  const [users, setUsers] = useState<UserData[]>([]);
  const [puestos, setPuestos] = useState<PuestoData[]>([]);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [puestosLoading, setPuestosLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [showCreatePuestoModal, setShowCreatePuestoModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false);
  const [showCreatePaymentModal, setShowCreatePaymentModal] = useState(false);

  // Ref for PaymentHistoryFolder to trigger refresh
  const paymentHistoryRef = useRef<PaymentHistoryFolderRef>(null);

  useEffect(() => {
    const fetchAllData = async () => {
      if (!localId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const localIdNum = parseInt(localId);
        
        // Fetch all data in parallel
        const [localRes, sessionRes, salesRes, historicalRes, usersRes, puestosRes] = await Promise.allSettled([
          getLocalByLocalId(localId),
          getLastLocalSessionByLocalId(localIdNum),
          getRealTimeSalesStatisticsByLocalId(localIdNum),
          getHistoricalSalesStatisticsByLocalId(localIdNum),
          getUsersByLocalId(localIdNum),
          getPuestosByLocalId(localId) // API expects string
        ]);
        
        // Process local data
        if (localRes.status === 'fulfilled' && localRes.value.status === 200) {
          setLocalData(localRes.value.data);
        } else {
          setLocalData({ local_id: localIdNum, local_name: `Local #${localId}`, state: true });
        }
        
        // Process session data
        if (sessionRes.status === 'fulfilled' && sessionRes.value.status === 200) {
          setSessionData(sessionRes.value.data);
        }
        
        // Process real-time sales
        if (salesRes.status === 'fulfilled' && salesRes.value.status === 200) {
          setRealTimeSales(salesRes.value.data);
        }
        
        // Process historical data
        if (historicalRes.status === 'fulfilled' && historicalRes.value.status === 200) {
          setHistoricalData(historicalRes.value.data || []);
        }
        
        // Process users
        if (usersRes.status === 'fulfilled' && usersRes.value.status === 200) {
          setUsers(usersRes.value.data || []);
        }

        // Process puestos
        if (puestosRes.status === 'fulfilled' && puestosRes.value.status === 200) {
          setPuestos(puestosRes.value.data || []);
        }
        
      } catch (err) {
        console.error('Error fetching local data:', err);
        setError('Error al cargar la información del local');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [localId]);

  // ============= Handlers =============
  const handleBack = () => navigate('/MedioPuntoAdmin/Locales');
  const handleEdit = () => console.log('Edit local clicked');
  
  const handleDeleteLocal = async () => {
    if (!localData) return;
    await DeleteLocalPermanently(localData.local_id);
    navigate('/MedioPuntoAdmin/Locales');
  };

  const handleToggleState = async () => {
    if (!localData) return;
    await ToggleLocalState(localData.local_id);
    // Update local state after toggle
    const newState = !isLocalActive(localData.state);
    setLocalData({ ...localData, state: newState });
    // Update users state as well
    setUsers(users.map(u => ({ ...u, state: newState ? 1 : 0 })));
  };

  // Refresh puestos after create/update/delete
  const refreshPuestos = async () => {
    if (!localId) return;
    setPuestosLoading(true);
    try {
      const res = await getPuestosByLocalId(localId);
      if (res.status === 200) {
        setPuestos(res.data || []);
      }
    } catch (err) {
      console.error('Error refreshing puestos:', err);
    } finally {
      setPuestosLoading(false);
    }
  };

  // Refresh users after create
  const refreshUsers = async () => {
    if (!localId) return;
    try {
      const res = await getUsersByLocalId(parseInt(localId));
      if (res.status === 200) {
        setUsers(res.data || []);
      }
    } catch (err) {
      console.error('Error refreshing users:', err);
    }
  };

  const handlePuestoCreated = () => {
    setShowCreatePuestoModal(false);
    refreshPuestos();
  };

  const handleUserCreated = () => {
    setShowCreateUserModal(false);
    refreshUsers();
  };

  const handlePlanCreated = () => {
    setShowCreatePlanModal(false);
  };

  const handlePaymentCreated = () => {
    setShowCreatePaymentModal(false);
    // Refresh payment history immediately
    paymentHistoryRef.current?.refreshPayments();
  };

  // ============= Computed Values =============
  const isLocalActive = (state: boolean | number): boolean => state === true || state === 1;
  
  const activeUsers = users.filter(u => u.state === 1).length;

  const historicalTotals = React.useMemo(() => {
    return {
      totalSessions: historicalData.length,
      totalGrossSales: historicalData.reduce((sum, s) => sum + (s.gross_sales || 0), 0),
      totalNetSales: historicalData.reduce((sum, s) => sum + (s.net_sales || 0), 0),
      totalReceipts: historicalData.reduce((sum, s) => sum + (s.valid_receipt_count || 0), 0),
      totalTips: historicalData.reduce((sum, s) => sum + (s.total_tip || 0), 0)
    };
  }, [historicalData]);

  // Transform data for components
  const transformedUsers = users.map(u => ({
    user_id: u.user_id,
    name: `${u.username} ${u.last_name}`,
    email: u.email,
    state: u.state,
    rol_name: u.rol_name,
    puesto_name: u.puesto_name,
    password: u.password
  }));

  const transformedSessions = historicalData.map(s => ({
    session_id: s.session_id,
    start_time: s.start_timestamp,
    end_time: s.end_timestamp,
    total_sales: s.gross_sales,
    total_tickets: s.valid_receipt_count
  }));

  const activeShift = sessionData?.shifts?.find(s => s.active);

  // ============= Loading / Error States =============
  if (loading) {
    return (
      <div className="local-page loading-state">
        <MiniLoadingSpinner />
        <p>Cargando información del local...</p>
      </div>
    );
  }

  if (error || !localData) {
    return (
      <div className="local-page error-state">
        <p>{error || 'No se encontró el local'}</p>
        <button onClick={handleBack}>Volver</button>
      </div>
    );
  }

  // ============= Render =============
  return (
    <div className="local-page">
      {/* Header */}
      <div className="local-page-header">
        <button className="back-button" onClick={handleBack}>
          <HiArrowLeft size={20} />
          <span>Volver a Locales</span>
        </button>
        
        <div className="header-actions">
          <button 
            className="create-plan-button"
            onClick={() => setShowCreatePlanModal(true)}
          >
            <HiOutlineDocumentText size={18} />
            <span>Crear Plan</span>
          </button>
          <button 
            className="create-payment-button"
            onClick={() => setShowCreatePaymentModal(true)}
          >
            <HiOutlineCurrencyDollar size={18} />
            <span>Agregar Pago</span>
          </button>
          <button 
            className={`toggle-button ${isLocalActive(localData.state) ? 'deactivate' : 'activate'}`} 
            onClick={() => setShowToggleModal(true)}
          >
            <HiOutlinePower size={18} />
            <span>{isLocalActive(localData.state) ? 'Desactivar' : 'Activar'}</span>
          </button>
          <button className="delete-button" onClick={() => setShowDeleteModal(true)}>
            <HiOutlineTrash size={18} />
            <span>Eliminar</span>
          </button>
          <button className="edit-button" onClick={handleEdit}>
            <HiOutlinePencilSquare size={18} />
            <span>Editar</span>
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <LocalHero
        name={localData.local_name}
        localId={localData.local_id}
        isActive={isLocalActive(localData.state)}
        totalUsers={users.length}
        activeUsers={activeUsers}
        totalSessions={historicalTotals.totalSessions}
        onStatusClick={() => setShowToggleModal(true)}
      />

      <div className="local-content">
        {/* Active Session */}
        <ActiveSessionCard
          isActive={sessionData?.active || false}
          sessionId={sessionData?.local_session_id}
          startedBy={sessionData?.username}
          startTime={sessionData?.session_start}
          activeShiftName={activeShift?.name}
          shifts={sessionData?.shifts?.map(s => ({
            shift_id: s.shift_id,
            shift_name: s.name,
            state: s.active,
            user_name: s.open_username
          }))}
          lastSessionDate={sessionData?.session_end || sessionData?.session_start}
        />

        {/* Real-time Sales - Only show when session is active */}
        {sessionData?.active && realTimeSales && (
          <SalesStatsCard
            title="Ventas en Tiempo Real"
            subtitle="Sesión Actual"
            type="realtime"
            stats={{
              grossSales: realTimeSales.gross_sales,
              netSales: realTimeSales.net_sales,
              tickets: realTimeSales.valid_receipt_count,
              averageTicket: realTimeSales.avg_ticket,
              tips: realTimeSales.total_tip,
              pendingPayments: realTimeSales.pending_payments_count
            }}
          />
        )}

        {/* Historical Summary */}
        <SalesStatsCard
          title="Resumen Histórico Total"
          type="historical"
          stats={{
            totalSessions: historicalTotals.totalSessions,
            grossSales: historicalTotals.totalGrossSales,
            tickets: historicalTotals.totalReceipts,
            tips: historicalTotals.totalTips
          }}
        />

        {/* Payment History Folder */}
        <PaymentHistoryFolder 
          ref={paymentHistoryRef}
          localId={localData.local_id}
          localName={localData.local_name}
        />

        {/* Users Grid with Puestos Subview */}
        <UsersGrid 
          users={transformedUsers}
          title="Usuarios del Local"
          onCreateClick={() => setShowCreateUserModal(true)}
          puestos={puestos}
          puestosLoading={puestosLoading}
          onCreatePuestoClick={() => setShowCreatePuestoModal(true)}
          onPuestosChange={refreshPuestos}
        />

        {/* Sessions Table */}
        <SessionsTable
          sessions={transformedSessions}
          title="Historial de Sesiones"
        />
      </div>

      {/* Delete Modal */}
      <DeleteLocalModal
        isOpen={showDeleteModal}
        localName={localData.local_name}
        localId={localData.local_id}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteLocal}
      />

      {/* Toggle State Modal */}
      <ToggleStateModal
        isOpen={showToggleModal}
        localName={localData.local_name}
        isCurrentlyActive={isLocalActive(localData.state)}
        usersCount={users.length}
        onClose={() => setShowToggleModal(false)}
        onConfirm={handleToggleState}
      />

      {/* Create Puesto Modal */}
      <CreatePuestoModal
        isOpen={showCreatePuestoModal}
        localId={localData.local_id}
        localName={localData.local_name}
        onClose={() => setShowCreatePuestoModal(false)}
        onSuccess={handlePuestoCreated}
      />

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={showCreateUserModal}
        localId={localData.local_id}
        localName={localData.local_name}
        puestos={puestos}
        onClose={() => setShowCreateUserModal(false)}
        onSuccess={handleUserCreated}
        onCreatePuesto={() => {
          setShowCreateUserModal(false);
          setShowCreatePuestoModal(true);
        }}
      />

      {/* Create Plan Modal */}
      <CreateLocalPlan
        show={showCreatePlanModal}
        onHide={() => setShowCreatePlanModal(false)}
        onPlanCreated={handlePlanCreated}
        localId={localData.local_id}
        localName={localData.local_name}
      />

      {/* Create Payment Modal */}
      <CreateLocalPlanPayment
        show={showCreatePaymentModal}
        onHide={() => setShowCreatePaymentModal(false)}
        onPaymentCreated={handlePaymentCreated}
        localId={localData.local_id}
        localName={localData.local_name}
      />
    </div>
  );
};

export default LocalPage;
